import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Eye, Star, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, absUploadUrl } from "../lib/apiClient";
import AdminShell from "./AdminShell";

export default function AdminOffersList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/offers");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load offers"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function remove(id, title) {
    if (!window.confirm(`Delete offer "${title}"? Existing leads are preserved.`)) return;
    try {
      await api.delete(`/admin/offers/${id}`);
      toast.success("Offer deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  }

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-oj-ink">All offers</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {loading ? "Loading..." : `${items.length} offer${items.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-new-offer-btn"
          onClick={() => nav("/admin/offers/new")}
          className="inline-flex items-center gap-2 bg-oj-ink text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-zinc-800"
        >
          <Plus className="size-4" /> New offer
        </button>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="admin-offers-table">
        {items.length === 0 && !loading ? (
          <div className="p-12 text-center">
            <div className="text-zinc-500 text-sm">No offers yet.</div>
            <button
              type="button"
              onClick={() => nav("/admin/offers/new")}
              className="mt-4 inline-flex items-center gap-2 bg-oj-brand text-oj-deep font-semibold text-sm px-4 py-2.5 rounded-md"
            >
              <Plus className="size-4" /> Create your first offer
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Offer</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Price</th>
                <th className="text-left px-4 py-3">Valid until</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} data-testid={`admin-offer-row-${o.slug}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {o.list_image_url ? (
                        <img src={absUploadUrl(o.list_image_url)} alt="" className="w-12 h-12 object-cover rounded-md border border-zinc-200" />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-zinc-100" />
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-oj-ink truncate">{o.title}</div>
                        <div className="text-xs text-zinc-500 truncate">
                          /offers/{o.slug}
                          {o.slug_fr && o.slug_fr !== o.slug && (
                            <span className="ml-2 text-zinc-400">· /offers/fr/{o.slug_fr}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          o.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"
                        }`}
                      >
                        {o.active ? "Active" : "Hidden"}
                      </span>
                      {o.featured && (
                        <span title="Featured" className="text-amber-500"><Star className="size-4" /></span>
                      )}
                      {o.is_popup && (
                        <span title="Popup" className="text-oj-brand"><Bell className="size-4" /></span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700">
                    {o.price > 0 ? `${o.currency} ${o.price}` : (o.discount_label || "—")}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">
                    {o.valid_until ? new Date(o.valid_until).toLocaleDateString() : "No expiry"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to={`/offers/${o.slug}`}
                        target="_blank"
                        title="Preview"
                        className="p-1.5 rounded hover:bg-zinc-100"
                      >
                        <Eye className="size-4 text-zinc-600" />
                      </Link>
                      <Link
                        to={`/admin/offers/${o.id}`}
                        data-testid={`admin-edit-offer-${o.slug}`}
                        title="Edit"
                        className="p-1.5 rounded hover:bg-zinc-100"
                      >
                        <Edit2 className="size-4 text-zinc-600" />
                      </Link>
                      <button
                        type="button"
                        data-testid={`admin-delete-offer-${o.slug}`}
                        title="Delete"
                        onClick={() => remove(o.id, o.title)}
                        className="p-1.5 rounded hover:bg-red-50"
                      >
                        <Trash2 className="size-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
